/**
 * ヘッドレスバトル（UIなしの即時シミュレーション）
 *
 * CPU同士の試合をスキップする際に使用する。
 * 既存のturnProcessor、AI、victoryConditionをそのまま活用する。
 */
import { BattleState, BattleResultType, DistanceType, StanceType, Monster, TurnResult } from '../types';
import { processTurn } from '../battle/turnProcessor';
import { selectCommands } from '../ai/aiSelector';
import { AILevel, AIConfig } from '../ai/types';
import { checkVictoryAfterTurn, checkVictoryOnTimeout } from '../battle/victoryCondition';

const MAX_TURNS = 30;
const MAX_DRAW_RETRIES = 3;

function createInitialState(m1: Monster, m2: Monster): BattleState {
  return {
    player1: {
      monsterId: m1.id,
      currentHp: m1.stats.hp,
      currentStance: StanceType.NORMAL,
      remainingSpecialCount: m1.stats.specialAttackCount,
      usedReflectCount: 0,
    },
    player2: {
      monsterId: m2.id,
      currentHp: m2.stats.hp,
      currentStance: StanceType.NORMAL,
      remainingSpecialCount: m2.stats.specialAttackCount,
      usedReflectCount: 0,
    },
    currentDistance: DistanceType.MID,
    currentTurn: 1,
    remainingTime: 120,
    isFinished: false,
  };
}

// Lv6 は async 必須のためヘッドレスバトルでは使用できない。Lv5 にフォールバック
function capLevel(level: AILevel): AILevel {
  return level === AILevel.LV6 ? AILevel.LV5 : level;
}

function runBattleOnce(
  monster1: Monster,
  monster2: Monster,
  aiLevel: AILevel,
  player1Config?: AIConfig,
  player2Config?: AIConfig,
): BattleResultType {
  let state = createInitialState(monster1, monster2);
  const turnHistory: TurnResult[] = [];

  const p1Level = capLevel(player1Config?.thinkingLevel ?? aiLevel);
  const p2Level = capLevel(player2Config?.thinkingLevel ?? aiLevel);

  for (let turn = 0; turn < MAX_TURNS; turn++) {
    const p1Cmds = selectCommands(state, 'player1', monster1, p1Level, Math.random, monster2, turnHistory, player1Config);
    const p2Cmds = selectCommands(state, 'player2', monster2, p2Level, Math.random, monster1, turnHistory, player2Config);

    const { newState, turnResult } = processTurn(state, monster1, monster2, p1Cmds, p2Cmds, Math.random);
    turnHistory.push(turnResult);
    state = newState;

    const victory = checkVictoryAfterTurn(state);
    if (victory) return victory.resultType;
  }

  return checkVictoryOnTimeout(state).resultType;
}

/**
 * 2体のモンスターをAI同士で対戦させ、結果を即時返す。
 *
 * @param monster1 モンスター1（player1側 = tournamentのparticipant1）
 * @param monster2 モンスター2（player2側 = tournamentのparticipant2）
 * @param aiLevel AI難易度（両者共通）
 * @returns PLAYER1_WIN または PLAYER2_WIN（DRAWは返さない）
 *   DRAWが発生した場合は最大MAX_DRAW_RETRIES回再試合する。
 *   それでもDRAWなら PLAYER1_WIN を返す（Issue #335）。
 */
export function simulateHeadlessBattle(
  monster1: Monster,
  monster2: Monster,
  aiLevel: AILevel,
  player1Config?: AIConfig,
  player2Config?: AIConfig,
): BattleResultType {
  for (let attempt = 0; attempt <= MAX_DRAW_RETRIES; attempt++) {
    const result = runBattleOnce(monster1, monster2, aiLevel, player1Config, player2Config);
    if (result !== BattleResultType.DRAW) return result;
  }
  return BattleResultType.PLAYER1_WIN;
}
